/**
 * @module app.shared
 */

import { encodeQueryString } from 'app/shared/lib/utilities';
import { QueryComparisonOperator, QueryStringComparisonOperator, QueryOperation, IQueryOperation, IApiQueryResults } from './api-types';

export function createQueryResults<T>(resultsType: string, results: T[] = [], isSuccessful: boolean = false): Promise<IApiQueryResults<T>> {
    const response: IApiQueryResults<T> = {
        $baseUrl: '',
        timestamp: null,
        isSuccessful,
        status: null,
        count: results.length,
        resultsType,
        results,
        links: []
    };
    return Promise.resolve(response);
}

export function encodeFieldName(value: string): string {
    return encodeQueryString(value);
}

export function escapeStringLiteral(value: string): string {
    return value.replace("'", "''");
}

export function encodeTaggedLiteral(dataTypeTag: string, value: string | null = null): string {
    if(value === null) {
        return 'null';
    }
    return encodeQueryString(`${dataTypeTag}'${escapeStringLiteral(value)}'`);
}

export function encodeTaggedLiteralArray(dataTypeTag: string, values: (string | null)[] = []): string {
    const results: string[] = [];
    results.push('[');
    let first = true;
    for(const value of values) {
        if(first) {
            first = false;
        }
        else {
            results.push(',');
        }
        if(value) {
            results.push(`${dataTypeTag}'${escapeStringLiteral(value)}'`);
        }
        else {
            results.push('null');
        }
    }
    results.push(']');
    return encodeQueryString(results.join(''));
}

export function encodeStringLiteral(value: string | null = null): string {
    return encodeTaggedLiteral('', value);
}

export function encodeStringLiteralArray(values: (string | null)[] = []): string {
    return encodeTaggedLiteralArray('', values);
}

export function encodeBooleanLiteral(value: boolean | null = null): string {
    if(value === true) { return 'true'; }
    if(value === false) { return 'false'; }
    return 'null';
}

const numericIdentifierRegex = /^\d+$/;
export function encodeIdentifierLiteral(value: string | null = null): string {
    if(value === null) {
        return 'null';
    }
    if(value.match(numericIdentifierRegex)) {
        return value;
    }
    return encodeQueryString(`'${escapeStringLiteral(value)}'`);
}

export function encodeIdentifierLiteralArray(values: (string | null)[] = []): string {
    const results: string[] = [];
    results.push('[');
    let first = true;
    for(const value of values) {
        if(first) {
            first = false;
        }
        else {
            results.push(',');
        }
        if(value) {
            if(value.match(numericIdentifierRegex)) {
                results.push(value);
            }
            else {
                results.push(`'${escapeStringLiteral(value)}'`);
            }
        }
        else {
            results.push('null');
        }
    }
    results.push(']');
    return encodeQueryString(results.join(''));
}

export function constArray<T>(...values: T[]): ReadonlyArray<T> {
    return Object.freeze(values);
}

const allQueryComparisonOperators = constArray<QueryComparisonOperator>('eq', 'ne', 'gt', 'lt', 'gte', 'lte');

export function isQueryComparisonOperator(operator: string): operator is QueryComparisonOperator {
    return !!allQueryComparisonOperators.find(op => op === operator);
}

const negatedOperators = {
    'eq': 'ne',
    'ne': 'eq',
    'lt': 'gte',
    'gt': 'lte',
    'lte': 'gt',
    'gte': 'lt'
};

export function mapQueryComparisonOperator(operator: QueryComparisonOperator, negate: boolean = false): IQueryOperation {
    if(negate) {
        return new QueryOperation(negatedOperators[operator] || 'ne', 'infix');
    }
    else {
        return new QueryOperation(operator || 'eq', 'infix');
    }
}

function removeNotOperatorPrefix(operator: string): string {
    return operator.substr(4, 1).toLowerCase() + operator.substr(5);
}
(window as any).dont = removeNotOperatorPrefix;

export function mapQueryStringComparisonOperator(operator: QueryStringComparisonOperator, negate: boolean = false): IQueryOperation {
    if(isQueryComparisonOperator(operator)) {
        return mapQueryComparisonOperator(operator, negate);
    }
    switch(operator) {
        case 'contains':
        case 'endsWith':
        case 'startsWith':
            return new QueryOperation(operator, 'function').negate(negate);
        case 'notContains':
        case 'notEndsWith':
        case 'notStartsWith':
            return new QueryOperation(removeNotOperatorPrefix(operator), 'function').negate(!negate);
    }
    return mapQueryComparisonOperator('eq', negate);
}
